<template>
  <div class="invoice-dense-tab">
    <app-collapse class="mb-1 invoice-dense-filters">
      <app-collapse-item title="Filters">
        <template #header>
          <span class="invoice-dense-filters__title">
            <feather-icon icon="FilterIcon" size="15" />
            <span>Filters</span>
          </span>
        </template>
        <div class="p-2">
          <b-row>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Start Date">
                <b-form-input v-model="filters.startDate" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="End Date">
                <b-form-input v-model="filters.endDate" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Client">
                <b-form-select v-model="filters.client" :options="clientSelectOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Industry (Vertical)">
                <b-form-select v-model="filters.industry" :options="industrySelectOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Candidate Status (ATS)">
                <b-form-select v-model="filters.candidateStatus" :options="candidateStatusSelectOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Category">
                <b-form-select v-model="filters.category" :options="categorySelectOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3" class="d-flex align-items-end">
              <b-button variant="primary" size="sm" @click="applyFilters" class="mr-2">Apply</b-button>
              <b-button variant="outline-secondary" size="sm" @click="clearFilters">Clear</b-button>
            </b-col>
          </b-row>
        </div>
      </app-collapse-item>
    </app-collapse>

    <div class="invoice-dense-toolbar">
      <div class="invoice-dense-toolbar__search">
        <b-input-group size="sm">
          <template #prepend>
            <b-input-group-text class="invoice-dense-search-icon">
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-text>
          </template>
          <b-form-input v-model="search" placeholder="Search candidate, client…" :debounce="200" />
          <template v-if="search" #append>
            <b-button size="sm" variant="outline-secondary" @click="search = ''">
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </template>
        </b-input-group>
      </div>
      <div class="invoice-dense-toolbar__meta">
        <span v-if="!loading">{{ filteredItems.length }} record{{ filteredItems.length === 1 ? '' : 's' }}</span>
        <span v-else>Loading…</span>
      </div>
    </div>

    <div class="invoice-dense-table-wrap">
      <b-table
        :items="filteredItems"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        hover
        responsive
        :busy="loading"
        show-empty
        class="invoice-dense-table mb-0"
      >
        <template #table-busy>
          <div class="invoice-dense-state invoice-dense-state--loading">
            <b-spinner small class="mr-50" />
            <span>Loading candidate revenue…</span>
          </div>
        </template>
        <template #empty>
          <div class="invoice-dense-state">
            <div class="invoice-dense-empty-icon">
              <feather-icon icon="UsersIcon" size="28" />
            </div>
            <h6 class="mb-25">No candidate revenue data</h6>
            <p class="text-muted mb-1">
              {{ search ? 'Try a different search or clear filters.' : 'Adjust filters to load candidate revenue.' }}
            </p>
            <b-button v-if="search" size="sm" variant="outline-secondary" @click="search = ''">Clear search</b-button>
          </div>
        </template>
        <template #cell(candidate_name)="{ item }">
          <div class="inv-cell-stack">
            <b-link class="inv-invoice-link" @click="$emit('view-candidate', item.candidate_id)">{{ item.candidate_name || 'N/A' }}</b-link>
            <small v-if="formatJoiningDate(item)" class="text-muted">{{ formatJoiningDate(item) }}</small>
          </div>
        </template>
        <template #cell(client_name)="{ item }">
          <div class="inv-cell-stack">
            <span class="inv-primary-text">{{ item.client_name || 'N/A' }}</span>
            <small v-if="item.position_name" class="text-muted">{{ item.position_name }}</small>
          </div>
        </template>
        <template #cell(invoice_number)="{ item }">
          <template v-if="item.invoice_number && item.invoice_number !== 'N/A'">
            <template v-if="item._invoicesForCandidate && item._invoicesForCandidate.length">
              <span v-for="(inv, idx) in item._invoicesForCandidate" :key="inv.id || idx">
                <b-link @click="$emit('view-invoice', inv)" class="inv-invoice-link mr-1">{{ inv.invoice_id || inv.invoice_number }}</b-link>
                <span v-if="idx < item._invoicesForCandidate.length - 1">, </span>
              </span>
            </template>
            <span v-else>{{ item.invoice_number }}</span>
          </template>
          <span v-else class="text-muted">N/A</span>
        </template>
        <template #cell(revenue_amount)="{ item }">
          <span class="inv-amount">₹{{ formatIndianNumber(item.revenue_amount || 0) }}</span>
        </template>
        <template #cell(billing_model)="{ item }">
          <span class="inv-soft-badge">{{ getBillingModelShortLabel(item.billing_model) }}</span>
        </template>
        <template #cell(billing_period)="{ item }">{{ formatDate(item.billing_period) }}</template>
        <template #cell(actions)="{ item }">
          <div class="inv-row-actions">
            <b-button
              v-if="canUpdateCandidateRevenue(item)"
              variant="flat-primary"
              size="sm"
              class="btn-icon"
              aria-label="Update Revenue"
              @click="$emit('update-revenue', item)"
            >
              <feather-icon icon="EditIcon" size="16" />
            </b-button>
            <span v-else class="text-muted small">—</span>
          </div>
        </template>
      </b-table>
    </div>

    <div v-if="filteredItems.length > perPage" class="invoice-dense-pagination">
      <b-pagination v-model="currentPage" :total-rows="filteredItems.length" :per-page="perPage" align="right" size="sm" class="mb-0" />
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupText, BTable, BButton, BLink, BSpinner, BPagination } from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import moment from 'moment'
import invoiceFormatMixin from '@/mixins/invoiceFormatMixin'

function thisMonthRange() {
  return {
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  }
}

function toSelectOptions(list, emptyText) {
  const mapped = (list || []).map(o => {
    if (typeof o !== 'object' || o == null) return { value: o, text: String(o) }
    const text = o.text || o.title || o.label || String(o.value ?? '')
    const value = Object.prototype.hasOwnProperty.call(o, 'value')
      ? o.value
      : (o.title != null ? o.title : text)
    return { value, text }
  })
  return emptyText != null ? [{ value: null, text: emptyText }, ...mapped] : mapped
}

function collectVerticals(row) {
  if (!row || typeof row !== 'object') return []
  const vals = [
    row.vertical,
    row.industry,
    row.department,
    row.client_vertical,
    row.job_vertical,
    row.clientVertical,
    row.jobVertical
  ]
  const invoices = row._invoicesForCandidate
  if (Array.isArray(invoices)) {
    for (const inv of invoices) {
      if (!inv) continue
      vals.push(inv.vertical, inv.industry, inv.department, inv.client_vertical)
    }
  }
  return vals
}

function matchesIndustryFilter(row, industry) {
  const wanted = String(industry || '').trim().toLowerCase()
  if (!wanted) return true
  const vals = collectVerticals(row).map(v => String(v || '').trim()).filter(Boolean)
  // No vertical/department on the row: keep it (API may already have filtered)
  if (!vals.length) return true
  return vals.some(v => v.toLowerCase() === wanted)
}

export default {
  name: 'CandidateRevenueTab',
  components: { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupText, BTable, BButton, BLink, BSpinner, BPagination, AppCollapse, AppCollapseItem },
  mixins: [invoiceFormatMixin],
  props: {
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    loading: Boolean,
    perPage: { type: Number, default: 20 },
    clients: { type: Array, default: () => [] },
    industryOptions: { type: Array, default: () => [] },
    candidateStatusOptions: { type: Array, default: () => [] },
    categoryOptions: { type: Array, default: () => [] },
    initialFilters: {
      type: Object,
      default: () => ({
        startDate: null,
        endDate: null,
        client: null,
        industry: null,
        candidateStatus: '',
        category: ''
      })
    }
  },
  data() {
    return {
      search: '',
      currentPage: 1,
      filters: {
        startDate: this.initialFilters.startDate || null,
        endDate: this.initialFilters.endDate || null,
        client: this.initialFilters.client || null,
        industry: this.initialFilters.industry || null,
        candidateStatus: this.initialFilters.candidateStatus != null ? this.initialFilters.candidateStatus : '',
        category: this.initialFilters.category != null ? this.initialFilters.category : ''
      }
    }
  },
  computed: {
    clientSelectOptions() {
      return toSelectOptions(this.clients, 'Select Client')
    },
    industrySelectOptions() {
      return toSelectOptions(this.industryOptions, 'Select Industry')
    },
    candidateStatusSelectOptions() {
      return toSelectOptions(this.candidateStatusOptions)
    },
    categorySelectOptions() {
      return toSelectOptions(this.categoryOptions)
    },
    filteredItems() {
      let data = [...this.items]
      if (this.filters.client) data = data.filter(i => i.client_name === this.filters.client)
      if (this.filters.industry) data = data.filter(i => matchesIndustryFilter(i, this.filters.industry))
      if (this.filters.candidateStatus) data = data.filter(i => i.candidate_status === this.filters.candidateStatus)
      if (this.filters.category) data = data.filter(i => i.category === this.filters.category)
      if (this.search) {
        const q = this.search.toLowerCase()
        data = data.filter(i => (i.candidate_name || '').toLowerCase().includes(q) || (i.client_name || '').toLowerCase().includes(q))
      }
      return data
    }
  },
  watch: {
    search() { this.currentPage = 1 },
    items() { this.currentPage = 1 },
    filters: {
      deep: true,
      handler() { this.currentPage = 1 }
    }
  },
  methods: {
    formatJoiningDate(item) {
      const raw = item && (item.joining_date || item.doj || item.date_of_joining || item.joiningDate)
      if (raw == null || raw === '') return ''
      const m = moment(raw)
      return m.isValid() ? `Joining: ${m.format('DD-MMM-YYYY')}` : ''
    },
    applyFilters() { this.$emit('apply-filters', { ...this.filters }) },
    clearFilters() {
      const range = thisMonthRange()
      this.filters = {
        startDate: range.startDate,
        endDate: range.endDate,
        client: null,
        industry: null,
        candidateStatus: '',
        category: ''
      }
      this.$emit('apply-filters', { ...this.filters })
    }
  }
}
</script>

<style lang="scss">
@import './invoice-dense-tab.scss';
</style>
